import { Button, type ButtonProps } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { TooltipContentProps } from '@radix-ui/react-tooltip'
import { useTranslate, type TranslateParams, type TranslationKey } from '@tolgee/react'
import { cva, VariantProps } from 'class-variance-authority'
import React from 'react'

type TooltipIconButtonProps = Omit<ButtonProps, 'variant' | 'size'> &
  VariantProps<typeof tooltipIconButtonVariants> & {
    delayDuration?: number
    contentProps?: TooltipContentProps
  } & (
    | {
        keyName: TranslationKey
        params?: TranslateParams
        titleString?: never
      }
    | {
        keyName?: never
        params?: never
        titleString: string
      }
  )

const TooltipIconButton = React.forwardRef<HTMLButtonElement, TooltipIconButtonProps>(
  (
    {
      children,
      className,
      keyName,
      titleString,
      params,
      size,
      delayDuration = 0,
      contentProps,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslate()
    const title = titleString ?? t(keyName, params)
    return (
      <Tooltip delayDuration={delayDuration}>
        <TooltipTrigger asChild>
          <Button
            ref={ref}
            unstyled
            aria-label={title}
            className={cn(tooltipIconButtonVariants({ size }), className)}
            {...props}
          >
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent size='sm' {...contentProps}>
            {title}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    )
  },
)

const tooltipIconButtonVariants = cva(
  'flex select-none shrink-0 items-center justify-center hover:bg-neutral-75 focus:bg-neutral-75 active:bg-neutral-75 rounded-full outline-none transition-colors ease-in-out disabled:pointer-events-none disabled:opacity-60 [&>svg]:transition-transform [&:active>svg]:scale-90',
  {
    variants: {
      size: {
        sm: 'size-6 [&>svg]:size-5',
        xs: 'size-5 [&>svg]:size-4',
        auto: '',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
)

export { TooltipIconButton, tooltipIconButtonVariants }
